import type {
  CkAggregateMetrics,
  CkSnapshot,
  CkSnapshotOutcome,
  CkWindowOutcome,
} from './ck-debug.types';

export interface CkSnapshotFilters {
  app: string;
  trigger: string;
  outcome: string;
  searchTerm: string;
}

export const CK_DEBUG_TRIGGER_OPTIONS = [
  'appActivation',
  'periodic',
  'windowTitleChange',
  'manual',
  'input',
  'appBackgrounded',
] as const;

const SNAPSHOT_OUTCOME_DISPLAY: Record<
  CkSnapshotOutcome,
  { bg: string; text: string; label: string }
> = {
  success: { bg: 'bg-success-3', text: 'text-success-11', label: 'OK' },
  failed: { bg: 'bg-danger-3', text: 'text-danger-11', label: 'FAIL' },
  timeout: { bg: 'bg-warning-3', text: 'text-warning-11', label: 'TIME' },
  cancelled: { bg: 'bg-warning-3', text: 'text-warning-11', label: 'CNCL' },
  empty: {
    bg: 'bg-surface-hover',
    text: 'text-foreground-muted',
    label: 'EMPTY',
  },
  partial_timeout: {
    bg: 'bg-warning-3',
    text: 'text-warning-11',
    label: 'P.TIME',
  },
};

const WINDOW_OUTCOME_DISPLAY: Record<
  CkWindowOutcome,
  { dot: string; label: string }
> = {
  included: { dot: 'bg-success-9', label: 'Included' },
  no_result: { dot: 'bg-foreground-muted', label: 'No Result' },
  skipped: { dot: 'bg-warning-9', label: 'Skipped' },
  failed: { dot: 'bg-danger-9', label: 'Failed' },
};

export const CK_DEBUG_OUTCOME_OPTIONS = Object.keys(
  SNAPSHOT_OUTCOME_DISPLAY
) as CkSnapshotOutcome[];

export function getSnapshotOutcomeDisplay(outcome: string) {
  return (
    SNAPSHOT_OUTCOME_DISPLAY[outcome as CkSnapshotOutcome] ?? {
      bg: 'bg-surface-hover',
      text: 'text-foreground-muted',
      label: outcome,
    }
  );
}

export function getWindowOutcomeDisplay(outcome: CkWindowOutcome) {
  return WINDOW_OUTCOME_DISPLAY[outcome];
}

export function filterSnapshots(
  snapshots: CkSnapshot[],
  filters: CkSnapshotFilters
): CkSnapshot[] {
  const searchTerm = filters.searchTerm.trim().toLowerCase();

  return snapshots.filter((snapshot) => {
    if (filters.app && snapshot.appName !== filters.app) return false;
    if (filters.trigger && snapshot.trigger !== filters.trigger) return false;
    if (filters.outcome && snapshot.outcome !== filters.outcome) return false;
    if (!searchTerm) return true;

    return buildSnapshotSearchText(snapshot).includes(searchTerm);
  });
}

export function getAvailableApps(snapshots: CkSnapshot[]) {
  const counts = new Map<string, number>();

  for (const snapshot of snapshots) {
    counts.set(snapshot.appName, (counts.get(snapshot.appName) ?? 0) + 1);
  }

  return Array.from(counts.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
}

export function computeAggregateMetrics(
  snapshots: CkSnapshot[]
): CkAggregateMetrics {
  const total = snapshots.length;
  const successCount = snapshots.filter(
    (snapshot) => snapshot.outcome === 'success'
  ).length;
  const failedCount = snapshots.filter(
    (snapshot) =>
      snapshot.outcome === 'failed' || snapshot.outcome === 'timeout'
  ).length;

  const avgDuration =
    total > 0
      ? snapshots.reduce(
          (sum, snapshot) => sum + snapshot.metrics.totalDurationMs,
          0
        ) / total
      : 0;

  let snapshotsPerMinute = 0;
  if (total >= 2) {
    const newest = snapshots[0].completedAt;
    const oldest = snapshots[total - 1].completedAt;
    const windowMs = newest - oldest;

    if (windowMs > 0) {
      snapshotsPerMinute = (total / windowMs) * 60000;
    }
  }

  const totalTraversedDedup = snapshots.reduce(
    (sum, snapshot) => sum + snapshot.metrics.traversedItemsDeduplicated,
    0
  );
  const totalParsedDedup = snapshots.reduce(
    (sum, snapshot) => sum + snapshot.metrics.parsedItemsDeduplicated,
    0
  );
  const totalScreenshotDedup = snapshots.reduce(
    (sum, snapshot) => sum + snapshot.metrics.screenshotsDeduplicated,
    0
  );
  const dedupedSnapshotCount = snapshots.filter((snapshot) => {
    const snapshotDedupTotal =
      snapshot.metrics.traversedItemsDeduplicated +
      snapshot.metrics.parsedItemsDeduplicated +
      snapshot.metrics.screenshotsDeduplicated;
    return snapshotDedupTotal > 0;
  }).length;
  const dedupRate = total > 0 ? (dedupedSnapshotCount / total) * 100 : 0;

  return {
    totalSnapshots: total,
    successCount,
    failedCount,
    snapshotsPerMinute: Math.round(snapshotsPerMinute * 10) / 10,
    avgDurationMs: Math.round(avgDuration),
    dedupedSnapshotCount,
    totalTraversedDedup,
    totalParsedDedup,
    totalScreenshotDedup,
    dedupRate: Math.round(dedupRate * 10) / 10,
    successRate: total > 0 ? Math.round((successCount / total) * 1000) / 10 : 0,
  };
}

function buildSnapshotSearchText(snapshot: CkSnapshot) {
  return `${snapshot.appName} ${snapshot.bundleId} ${snapshot.windows
    .map((window) => `${window.title} ${window.domain ?? ''}`)
    .join(' ')}`.toLowerCase();
}
